import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Download, FileSpreadsheet, FileText, Loader2,
  Palmtree, Pencil, ArrowRight, RefreshCw, AlertCircle, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { erpApi, formatApiErrorDetail } from "@/lib/erpApi";
import { useErpAuth } from "@/context/ErpAuthContext";

const MONTHS_SHORT = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_FULL = ["", "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];

const SUB_TABS = [
  { id: "CO", label: "CO - Odihnă", dot: "bg-green-600", active: "bg-green-600 text-white", val: "text-green-600" },
  { id: "CM", label: "CM - Medical", dot: "bg-orange-500", active: "bg-orange-500 text-white", val: "text-orange-600" },
  { id: "CFP", label: "CFP - Fără plată", dot: "bg-purple-600", active: "bg-purple-600 text-white", val: "text-purple-600" },
  { id: "CNC", label: "CNC - Naștere copil", dot: "bg-pink-600", active: "bg-pink-600 text-white", val: "text-pink-600" },
  { id: "CD", label: "CD - Deces", dot: "bg-stone-600", active: "bg-stone-600 text-white", val: "text-stone-600" },
];

export default function ConcediiTab() {
  const { hasPerm } = useErpAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [filter, setFilter] = useState("activi");
  const [subTab, setSubTab] = useState("CO");
  const [data, setData] = useState({ employees: [] });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [transferStatus, setTransferStatus] = useState(null);

  const [detailDialog, setDetailDialog] = useState({ open: false, name: "", month: null, tip: "CO", intervals: [], total: 0 });
  const [soldDialog, setSoldDialog] = useState({ open: false, employeeId: null, name: "", value: 0, saving: false });
  const [transferDialog, setTransferDialog] = useState({ open: false, fromYear: null, toYear: null, loading: false, result: null });

  const canEditSold = hasPerm("concedii.editare_sold");
  const canExport = hasPerm("concedii.vizualizare");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await erpApi.get(`/concedii/sumar/${year}?filter=${filter}`);
      setData(res.data || { employees: [] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [year, filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    erpApi.get(`/concedii/transfer-status/${year}`)
      .then((r) => { if (active) setTransferStatus(r.data); })
      .catch(() => { if (active) setTransferStatus(null); });
    return () => { active = false; };
  }, [year]);

  const openDetail = async (emp, month) => {
    try {
      const r = await erpApi.get(`/concedii/detalii/${emp.id}/${year}/${month}?tip=${subTab}`);
      setDetailDialog({
        open: true, name: emp.name, month, tip: subTab,
        intervals: r.data.intervals || [], total: r.data.total_days || 0,
      });
    } catch {
      toast.error("Nu am putut încărca detaliile");
    }
  };

  const saveSold = async () => {
    setSoldDialog((p) => ({ ...p, saving: true }));
    try {
      await erpApi.post("/concedii/sold", {
        employee_id: soldDialog.employeeId, an: year, sold_an_anterior: soldDialog.value,
      });
      toast.success("Sold actualizat");
      setSoldDialog((p) => ({ ...p, open: false, saving: false }));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      setSoldDialog((p) => ({ ...p, saving: false }));
    }
  };

  const runTransfer = async () => {
    setTransferDialog((p) => ({ ...p, loading: true }));
    try {
      const r = await erpApi.post(`/concedii/transfer-sold/${transferDialog.fromYear}`);
      setTransferDialog((p) => ({ ...p, loading: false, result: r.data }));
      load();
      erpApi.get(`/concedii/transfer-status/${year}`).then((s) => setTransferStatus(s.data)).catch(() => {});
    } catch (err) {
      setTransferDialog((p) => ({
        ...p, loading: false,
        result: { success: false, message: formatApiErrorDetail(err?.response?.data?.detail) },
      }));
    }
  };

  const download = async (type) => {
    setExporting(true);
    try {
      const res = await erpApi.get(`/concedii/export/${type}/${year}?filter=${filter}&tip=${subTab}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `concedii_${subTab}_${year}.${type === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${type.toUpperCase()} realizat`);
    } catch {
      toast.error("Export eșuat");
    } finally {
      setExporting(false);
    }
  };

  const cfg = SUB_TABS.find((t) => t.id === subTab);
  const employees = data.employees || [];
  const byMonthKey = `${subTab.toLowerCase()}_by_month`;
  const totalKey = `total_${subTab.toLowerCase()}`;
  const isCO = subTab === "CO";

  return (
    <div className="space-y-4" data-testid="erp-concedii-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="icon" onClick={() => setYear(year - 1)} data-testid="concedii-prev-year" className="h-9 w-9"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="font-bold text-lg min-w-[64px] text-center" data-testid="concedii-year">{year}</span>
          <Button variant="outline" size="icon" onClick={() => setYear(year + 1)} data-testid="concedii-next-year" className="h-9 w-9"><ChevronRight className="w-4 h-4" /></Button>

          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 ml-2">
            {[["activi", "Activi"], ["fosti", "Foști"], ["toti", "Toți"]].map(([id, lbl]) => (
              <button key={id} onClick={() => setFilter(id)} data-testid={`concedii-filter-${id}`}
                className={`h-8 px-3 rounded-md text-sm font-semibold transition-colors ${filter === id ? "bg-[#2563EB] text-white" : "text-slate-600 hover:bg-slate-200"}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {isCO && canEditSold && (
            <Button variant="outline" onClick={() => setTransferDialog({ open: true, fromYear: year - 1, toYear: year, loading: false, result: null })}
              data-testid="concedii-transfer-btn" className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50">
              <ArrowRight className="w-4 h-4" /> Transfer sold {year - 1} → {year}
            </Button>
          )}
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2" disabled={exporting} data-testid="concedii-export-btn">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => download("pdf")} data-testid="concedii-export-pdf"><FileText className="w-4 h-4 mr-2 text-red-600" /> Export PDF</DropdownMenuItem>
                <DropdownMenuItem onClick={() => download("excel")} data-testid="concedii-export-excel"><FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" /> Export Excel</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Transfer status */}
      {isCO && transferStatus?.transferred && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700" data-testid="concedii-transfer-status">
          <CheckCircle2 className="w-4 h-4" />
          Soldul din {transferStatus.transferred_from} a fost transferat în {year}
          {transferStatus.transferred_by ? ` de ${transferStatus.transferred_by}` : ""}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit flex-wrap">
        {SUB_TABS.map((t) => (
          <button key={t.id} onClick={() => setSubTab(t.id)} data-testid={`concedii-subtab-${t.id}`}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${subTab === t.id ? t.active : "text-slate-600 hover:bg-slate-200"}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${t.dot}`} />{t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Palmtree className="w-4 h-4 text-green-600" />
        Situație anuală derivată din Pontaj. Pentru a înregistra zile de concediu, folosește tab-ul <strong>Pontaj</strong>.
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="concedii-table-wrap">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-slate-100">
                <th className="px-2 py-2 text-left border font-semibold text-slate-700 w-16">Nr.Ctr</th>
                <th className="px-2 py-2 text-left border font-semibold text-slate-700 min-w-[160px]">Angajat</th>
                {isCO && <th className="px-2 py-2 text-center border font-semibold text-slate-700 w-16 bg-amber-50">Sold<br />{year - 1}</th>}
                {MONTHS_SHORT.map((m) => <th key={m} className="px-1 py-2 text-center border font-semibold text-slate-600 w-10">{m}</th>)}
                {isCO ? (
                  <>
                    <th className="px-2 py-2 text-center border font-semibold text-slate-700 bg-blue-50">Luate<br />{year}</th>
                    <th className="px-2 py-2 text-center border font-semibold text-slate-700 bg-green-50">Disp.<br />azi</th>
                    {filter !== "fosti" && <th className="px-2 py-2 text-center border font-semibold text-slate-700 bg-green-50">Disp.<br />sf.an</th>}
                  </>
                ) : (
                  <th className="px-2 py-2 text-center border font-semibold text-slate-700 bg-blue-50">Total<br />{year}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr><td colSpan={isCO ? (filter === "fosti" ? 16 : 17) : 15} className="px-4 py-10 text-center text-slate-400">Nu există date de concediu pentru acest an.</td></tr>
              ) : employees.map((emp, idx) => {
                const monthData = emp[byMonthKey] || {};
                const total = emp[totalKey] || 0;
                return (
                  <tr key={emp.id} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`concedii-row-${emp.id}`}>
                    <td className="px-2 py-1.5 border text-center text-slate-600">{emp.numar_contract || "-"}</td>
                    <td className="px-2 py-1.5 border font-medium">
                      {emp.name}
                      {emp.is_former && <span className="ml-1 text-xs text-red-500" title={`Fost angajat: ${(emp.pontaj_pana_la || "").slice(0, 10)}`}>(F)</span>}
                    </td>
                    {isCO && (
                      <td className={`px-2 py-1.5 border text-center bg-amber-50 ${canEditSold ? "cursor-pointer hover:bg-amber-100" : ""}`}
                        onClick={() => canEditSold && setSoldDialog({ open: true, employeeId: emp.id, name: emp.name, value: emp.sold_an_anterior || 0, saving: false })}
                        data-testid={`concedii-sold-${emp.id}`}>
                        <span className="font-medium">{emp.sold_an_anterior || 0}</span>
                        {canEditSold && <Pencil className="w-3 h-3 inline ml-1 text-slate-400" />}
                      </td>
                    )}
                    {MONTHS_SHORT.map((_, mi) => {
                      const month = mi + 1;
                      const val = monthData[month] ?? monthData[String(month)] ?? 0;
                      const hasValue = val > 0;
                      return (
                        <td key={month} className={`px-1 py-1.5 border text-center ${hasValue ? "cursor-pointer hover:bg-blue-50" : ""}`}
                          onClick={() => hasValue && openDetail(emp, month)}
                          data-testid={`concedii-cell-${emp.id}-${month}`}
                          title={hasValue ? "Click pentru detalii" : ""}>
                          {hasValue ? <span className={`font-semibold ${cfg.val}`}>{val}</span> : <span className="text-slate-300">-</span>}
                        </td>
                      );
                    })}
                    {isCO ? (
                      <>
                        <td className="px-2 py-1.5 border text-center bg-blue-50 font-semibold" data-testid={`concedii-total-${emp.id}`}>{total}</td>
                        <td className={`px-2 py-1.5 border text-center font-bold ${emp.disp_azi < 0 ? "bg-red-100 text-red-600" : "bg-green-50 text-green-700"}`}>{emp.disp_azi}</td>
                        {filter !== "fosti" && <td className={`px-2 py-1.5 border text-center font-bold ${emp.disp_sf_an < 0 ? "bg-red-100 text-red-600" : "bg-green-50 text-green-700"}`}>{emp.disp_sf_an}</td>}
                      </>
                    ) : (
                      <td className="px-2 py-1.5 border text-center bg-blue-50 font-semibold" data-testid={`concedii-total-${emp.id}`}>{total}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      {isCO && (
        <div className="flex flex-wrap gap-4 text-xs text-slate-500 border-t pt-3">
          <span><strong>Disp. azi</strong> = Sold + (luni trecute × zile anuale/12) − Luate <em>(rotunjit în sus la zi întreagă)</em></span>
          <span><strong>Disp. sf.an</strong> = Sold + zile anuale − Luate</span>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(o) => !o && setDetailDialog((p) => ({ ...p, open: false }))}>
        <DialogContent className="max-w-md" data-testid="concedii-detail-dialog">
          <DialogHeader>
            <DialogTitle>Detalii {MONTHS_FULL[detailDialog.month]} {year}</DialogTitle>
            <DialogDescription>{detailDialog.name} — {detailDialog.tip}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {detailDialog.intervals.length > 0 ? (
              <>
                {detailDialog.intervals.map((iv, i) => (
                  <div key={i} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="font-medium">{iv.formatted}</span>
                    <span className={`px-2 py-1 rounded text-white text-sm ${cfg.dot}`}>{iv.days} {iv.days === 1 ? "zi" : "zile"}</span>
                  </div>
                ))}
                <div className="border-t pt-2 flex justify-between"><span className="font-semibold">Total:</span><span className="font-bold">{detailDialog.total} {detailDialog.total === 1 ? "zi" : "zile"}</span></div>
              </>
            ) : <p className="text-center text-slate-500 py-4">Nu există înregistrări.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Sold edit dialog */}
      <Dialog open={soldDialog.open} onOpenChange={(o) => !o && setSoldDialog((p) => ({ ...p, open: false }))}>
        <DialogContent className="max-w-sm" data-testid="concedii-sold-dialog">
          <DialogHeader>
            <DialogTitle>Editare sold CO</DialogTitle>
            <DialogDescription>Sold an anterior pentru {soldDialog.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Zile CO rămase din {year - 1}</Label>
            <Input type="number" min="0" value={soldDialog.value} data-testid="concedii-sold-input"
              onChange={(e) => setSoldDialog((p) => ({ ...p, value: parseInt(e.target.value) || 0 }))} />
            <p className="text-xs text-slate-500">Zilele de concediu neefectuate în {year - 1} care se reportează în {year}.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSoldDialog((p) => ({ ...p, open: false }))}>Anulează</Button>
            <Button onClick={saveSold} disabled={soldDialog.saving} data-testid="concedii-sold-save">{soldDialog.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvează"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={transferDialog.open} onOpenChange={(o) => !o && setTransferDialog((p) => ({ ...p, open: false, result: null }))}>
        <DialogContent className="max-w-lg" data-testid="concedii-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowRight className="w-5 h-5 text-amber-600" /> Transfer sold concedii</DialogTitle>
            <DialogDescription>Reportează soldurile CO din {transferDialog.fromYear} în {transferDialog.toYear}</DialogDescription>
          </DialogHeader>
          {!transferDialog.result ? (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
                <div className="flex items-start gap-2"><AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <ul className="space-y-1">
                    <li>• Calculează zilele CO rămase la sfârșitul lui {transferDialog.fromYear}</li>
                    <li>• Le setează ca <strong>sold an anterior</strong> pentru {transferDialog.toYear}</li>
                    <li>• Soldurile negative devin 0; soldurile deja setate manual nu sunt suprascrise</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {transferDialog.result.success ? (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 text-green-700 font-medium"><CheckCircle2 className="w-5 h-5" />{transferDialog.result.message}</div>
                  {transferDialog.result.details?.length > 0 && (
                    <div className="max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 sticky top-0"><tr><th className="text-left p-2">Angajat</th><th className="text-right p-2">Sold {transferDialog.fromYear}</th><th className="text-right p-2">Anuale</th><th className="text-right p-2">Luate</th><th className="text-right p-2 font-bold">Sold nou</th></tr></thead>
                        <tbody>{transferDialog.result.details.map((d, i) => (
                          <tr key={i} className="border-b"><td className="p-2">{d.employee_name}</td><td className="text-right p-2">{d.sold_from_year}</td><td className="text-right p-2">{d.zile_co_anuale}</td><td className="text-right p-2">{d.total_co_taken}</td><td className="text-right p-2 font-bold text-green-600">{d.new_sold}</td></tr>
                        ))}</tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 font-medium"><AlertCircle className="w-5 h-5" />{transferDialog.result.message || "Eroare la transfer"}</div>
              )}
            </div>
          )}
          <DialogFooter>
            {!transferDialog.result ? (
              <>
                <Button variant="outline" onClick={() => setTransferDialog((p) => ({ ...p, open: false }))}>Anulează</Button>
                <Button onClick={runTransfer} disabled={transferDialog.loading} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="concedii-transfer-confirm">
                  {transferDialog.loading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Se transferă...</> : <><ArrowRight className="w-4 h-4" /> Transferă soldurile</>}
                </Button>
              </>
            ) : (
              <Button onClick={() => setTransferDialog((p) => ({ ...p, open: false, result: null }))}>Închide</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
